import React from "react";
import { Plus, Trash2, ArrowUpCircle, ArrowDownCircle } from "lucide-react";
import ApiClient from "../../api/ApiClient";

function uniqueBy(arr, key) {
  const seen = new Set();
  return arr.filter((item) => {
    const k = item[key];
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

function parseValue(val) {
  if (!val || val === "[]") return [];
  if (Array.isArray(val)) return val;
  try { return JSON.parse(val); } catch { return []; }
}

/*
 * CompFlowTaskPicker — selector de pares Flujo/Tarea para menús personalizados.
 *
 * Usa addEventListener nativo en lugar de onChange de React porque el diálogo
 * Syncfusion se renderiza fuera del árbol DOM de #root, y React 17 delega
 * eventos solo al root container, no a document/body.
 */
const CompFlowTaskPicker = ({ name, label, value, onChange }) => {
  const [catalog, setCatalog] = React.useState([]);
  const [loadingCatalog, setLoadingCatalog] = React.useState(true);
  const [pairs, setPairs] = React.useState(() => parseValue(value));
  const [error, setError] = React.useState("");
  const [selectedFlowId, setSelectedFlowId] = React.useState("");

  const containerRef = React.useRef(null);
  const flowRef = React.useRef(null);
  const taskRef = React.useRef(null);

  const emittingRef = React.useRef(false);
  const prevValueRef = React.useRef(value);

  /* stable refs so event handlers don't capture stale closures */
  const catalogRef = React.useRef(catalog);
  const pairsRef = React.useRef(pairs);
  const selectedFlowIdRef = React.useRef(selectedFlowId);
  const onChangeRef = React.useRef(onChange);
  const nameRef = React.useRef(name);

  React.useEffect(() => { catalogRef.current = catalog; }, [catalog]);
  React.useEffect(() => { pairsRef.current = pairs; }, [pairs]);
  React.useEffect(() => { selectedFlowIdRef.current = selectedFlowId; }, [selectedFlowId]);
  React.useEffect(() => { onChangeRef.current = onChange; }, [onChange]);
  React.useEffect(() => { nameRef.current = name; }, [name]);

  /* carga catálogo */
  React.useEffect(() => {
    let cancelled = false;
    setLoadingCatalog(true);
    ApiClient.doPost("/flows/get-flow-task-catalog", {})
      .then((res) => {
        if (!cancelled) {
          setCatalog(res?.result || []);
          setLoadingCatalog(false);
        }
      })
      .catch(() => { if (!cancelled) setLoadingCatalog(false); });
    return () => { cancelled = true; };
  }, []);

  /* sync externo: solo cuando el padre actualiza value (edit mode) */
  React.useEffect(() => {
    if (emittingRef.current) {
      emittingRef.current = false;
      prevValueRef.current = value;
      return;
    }
    if (value !== prevValueRef.current) {
      prevValueRef.current = value;
      setPairs(parseValue(value));
    }
  }, [value]);

  /* native change listener para el select de flujos.
   * Depende de loadingCatalog porque el <select> solo existe en el DOM
   * cuando loadingCatalog=false; en el primer render flowRef.current es null. */
  React.useEffect(() => {
    if (loadingCatalog) return;
    const sel = flowRef.current;
    if (!sel) return;
    const handler = (e) => {
      const fid = e.target.value;
      setSelectedFlowId(fid);
      setError("");
      if (taskRef.current) taskRef.current.value = "";
    };
    sel.addEventListener("change", handler);
    return () => sel.removeEventListener("change", handler);
  }, [loadingCatalog]);

  /* native click delegation en el contenedor para Agregar y Eliminar */
  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handler = (e) => {
      /* botón Agregar */
      if (e.target.closest("[data-action='add']")) {
        e.preventDefault();
        const fId = flowRef.current?.value || "";
        const tId = taskRef.current?.value || "";
        if (!fId || !tId) { setError("Seleccione un flujo y una tarea."); return; }

        const cat = catalogRef.current;
        const flowOpts = uniqueBy(cat, "FLOWID");
        const taskOpts = uniqueBy(cat.filter((x) => String(x.FLOWID) === String(fId)), "TASKID");

        const flow = flowOpts.find((x) => String(x.FLOWID) === String(fId));
        const task = taskOpts.find((x) => String(x.TASKID) === String(tId));
        if (!flow || !task) { setError("Par no encontrado."); return; }

        const current = pairsRef.current;
        if (current.some((p) => String(p.flowid) === String(fId) && String(p.taskid) === String(tId))) {
          setError("Ese par flujo / tarea ya fue agregado."); return;
        }
        setError("");
        if (flowRef.current) flowRef.current.value = "";
        if (taskRef.current) taskRef.current.value = "";
        setSelectedFlowId("");

        const next = [...current, { flowid: flow.FLOWID, flow: flow.FLOW, taskid: task.TASKID, task: task.TASK }];
        emittingRef.current = true;
        setPairs(next);
        if (typeof onChangeRef.current === "function") {
          onChangeRef.current({ target: { name: nameRef.current, value: JSON.stringify(next) } });
        }
        return;
      }

      /* botón Eliminar */
      const removeBtn = e.target.closest("[data-action='remove']");
      if (removeBtn) {
        e.preventDefault();
        const idx = parseInt(removeBtn.dataset.idx, 10);
        setError("");
        const next = pairsRef.current.filter((_, i) => i !== idx);
        emittingRef.current = true;
        setPairs(next);
        if (typeof onChangeRef.current === "function") {
          onChangeRef.current({ target: { name: nameRef.current, value: JSON.stringify(next) } });
        }
        return;
      }

      /* botones Subir / Bajar — reordenan el array de pares */
      const moveBtn = e.target.closest("[data-action='moveup'], [data-action='movedown']");
      if (moveBtn) {
        e.preventDefault();
        const idx = parseInt(moveBtn.dataset.idx, 10);
        const dir = moveBtn.dataset.action === "moveup" ? -1 : 1;
        const current = pairsRef.current;
        const swapIdx = idx + dir;
        if (swapIdx < 0 || swapIdx >= current.length) return;

        const next = current.slice();
        [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
        setError("");
        emittingRef.current = true;
        setPairs(next);
        if (typeof onChangeRef.current === "function") {
          onChangeRef.current({ target: { name: nameRef.current, value: JSON.stringify(next) } });
        }
      }
    };

    container.addEventListener("click", handler);
    return () => container.removeEventListener("click", handler);
  }, []);

  /* opciones para los selects (solo para renderizado inicial y actualización) */
  const flowOptions = React.useMemo(
    () => uniqueBy(catalog, "FLOWID").sort((a, b) => String(a.FLOW).localeCompare(String(b.FLOW))),
    [catalog]
  );

  const taskOptions = React.useMemo(
    () => uniqueBy(
            catalog.filter((x) => String(x.FLOWID) === String(selectedFlowId)),
            "TASKID"
          ).sort((a, b) => String(a.TASK).localeCompare(String(b.TASK))),
    [catalog, selectedFlowId]
  );

  return (
    <div className="xftp-root" ref={containerRef}>
      {label && <span className="xftp-label">{label}</span>}

      {loadingCatalog ? (
        <p className="xftp-list-empty">Cargando flujos...</p>
      ) : (
        <>
          <div className="xftp-picker-row">
            <div className="xftp-picker-col">
              <select ref={flowRef} defaultValue="" className="xftp-select">
                <option value="">Seleccione un flujo</option>
                {flowOptions.map((f) => (
                  <option key={f.FLOWID} value={f.FLOWID}>{f.FLOW}</option>
                ))}
              </select>
            </div>
            <div className="xftp-picker-col">
              <select
                ref={taskRef}
                defaultValue=""
                disabled={!selectedFlowId}
                className={"xftp-select" + (!selectedFlowId ? " xftp-select--disabled" : "")}
              >
                <option value="">Seleccione una tarea</option>
                {taskOptions.map((t) => (
                  <option key={t.TASKID} value={t.TASKID}>{t.TASK}</option>
                ))}
              </select>
            </div>
            <button type="button" data-action="add" className="xftp-add-btn">
              <Plus size={13} /> Agregar
            </button>
          </div>

          {error && <p className="xftp-error">{error}</p>}
        </>
      )}

      <div className="xftp-list">
        {pairs.length === 0 ? (
          <p className="xftp-list-empty">No hay flujos / tareas agregados.</p>
        ) : (
          pairs.map((p, i) => (
            <div key={`${p.flowid}_${p.taskid}_${i}`} className="xftp-list-row">
              <span className="xftp-list-flow">{p.flow}</span>
              <span className="xftp-list-sep">›</span>
              <span className="xftp-list-task">{p.task}</span>
              <div className="xftp-reorder-col">
                <button
                  type="button"
                  data-action="moveup"
                  data-idx={i}
                  className="xftp-reorder-btn"
                  title="Subir"
                  disabled={i === 0}
                >
                  <ArrowUpCircle size={18} />
                </button>
                <button
                  type="button"
                  data-action="movedown"
                  data-idx={i}
                  className="xftp-reorder-btn"
                  title="Bajar"
                  disabled={i === pairs.length - 1}
                >
                  <ArrowDownCircle size={18} />
                </button>
              </div>
              <button
                type="button"
                data-action="remove"
                data-idx={i}
                className="xftp-remove-btn"
                title="Eliminar"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))
        )}
      </div>

      {pairs.length > 0 && (
        <span className="xftp-count">
          {pairs.length} par{pairs.length !== 1 ? "es" : ""} agregado{pairs.length !== 1 ? "s" : ""}
        </span>
      )}
    </div>
  );
};

export default CompFlowTaskPicker;
